// A session handed to its agent's own CLI, in its Terminal pane, and back
// (Poulpe-555). One place drives a session at a time: while the CLI does,
// the composer says so and offers only the return. The runner chooses the
// CLI and its arguments; this sends only the driver the person saw.
import { useState } from 'react'
import type { Session } from '../api/types'
import { errorMessage } from '../api/client'
import { uuid4 } from '../api/uuid'
import { useBoardState, useStore } from '../store/context'

/** Whether the session's agent is in its own terminal, or on its way there or back. */
export function inTerminal(session: Session): boolean {
  const state = session.driver?.state
  return state === 'releasing' || state === 'native' || state === 'returning'
}

/** The terminal the agent's own CLI runs in: the handoff's job's id, while it drives. */
export function nativeTerminal(session: Session | undefined): string | null {
  return session?.driver?.state === 'native' ? session.driver.transition_id : null
}

const SAYS = {
  releasing: 'Opening its agent’s own terminal…',
  native: 'This session is in its agent’s own terminal, in the Terminal pane. Quit it there, or return it, to go on here.',
  returning: 'Coming back from its agent’s own terminal…',
} as const

export function TerminalHandoff({session, disabled}: {session: Session; disabled: boolean}) {
  const store = useStore()
  const { offline } = useBoardState()
  const [sending, setSending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const driver = session.driver
  if (!driver || session.origin.kind === 'review' || session.state === 'ended') return null
  const send = async (op: 'handoff_session' | 'return_session') => {
    setSending(true)
    setProblem(null)
    try {
      await store.sendSessionOp({mutation_id: uuid4(), op, args: {session_id: session.id, expected_generation: driver.generation, expected_revision: driver.revision}})
    } catch (error) { setProblem(errorMessage(error)) }
    finally { setSending(false) }
  }
  const failed = problem && <span role="alert" className="text-danger"> {problem}</span>
  if (driver.state === 'releasing' || driver.state === 'native' || driver.state === 'returning') {
    return <>
      <span role="status">{SAYS[driver.state]}</span>
      {driver.state === 'native' && <> <button type="button" className="button button-small" disabled={disabled || offline || sending} onClick={() => void send('return_session')}>{sending ? 'Returning…' : 'Return to the app'}</button></>}
      {failed}
    </>
  }
  if (!driver.native_supported || driver.state !== 'acp' || session.place?.kind === 'checkout') return null
  const blocked = disabled || offline || sending || session.state !== 'idle' || (session.queued?.length ?? 0) > 0 || !!session.pending_place
  return <>
    <button type="button" className="button button-small" disabled={blocked} title="Open the agent’s own CLI on this conversation, in the Terminal pane, for what only it offers" onClick={() => void send('handoff_session')}>{sending ? 'Opening…' : 'Open in terminal'}</button>
    {failed}
  </>
}
